import React, { useEffect, useRef } from 'react'
import FloatingLabel from 'react-bootstrap/FloatingLabel'
import Form from 'react-bootstrap/Form'

const GooglePlacesAutocomplete = ({
  value,
  onChange,
  onPlaceSelected,
  isInvalid,
  error,
  disabled = false,
}) => {
  const inputRef = useRef(null)
  const autocompleteRef = useRef(null)
  const scriptRef = useRef(null)

  useEffect(() => {
    const loadGoogleMapsScript = () => {
      const apiKey = document.body.getAttribute('data-google-maps-api-key')
      scriptRef.current = document.createElement('script')
      scriptRef.current.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async&callback=initGooglePlaces`
      scriptRef.current.async = true
      scriptRef.current.defer = true

      window.initGooglePlaces = () => {
        initAutocomplete()
        delete window.initGooglePlaces
      }

      document.head.appendChild(scriptRef.current)
    }

    if (!window.google) {
      loadGoogleMapsScript()
    } else {
      initAutocomplete()
    }

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current)
      }
      if (scriptRef.current) {
        document.head.removeChild(scriptRef.current)
      }
      if (window.initGooglePlaces) {
        delete window.initGooglePlaces
      }
    }
  }, [])

  const initAutocomplete = () => {
    if (!inputRef.current || !window.google) return

    autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: ['us', 'as', 'gu', 'mp', 'pr', 'um', 'vi'] },
      fields: ['address_components'],
      types: ['address'],
    })

    // Prevent form submission on enter
    inputRef.current.addEventListener('keypress', e => {
      if (e.key === 'Enter') {
        e.preventDefault()
      }
    })

    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace()
      const address = {
        address_line1: '',
        city: '',
        state: '',
        zip_code: '',
      }

      for (const component of place.address_components) {
        const componentType = component.types[0]

        switch (componentType) {
          case 'street_number':
            address.address_line1 = `${component.long_name} ${address.address_line1}`
            break
          case 'route':
            address.address_line1 += component.short_name
            break
          case 'postal_code':
            address.zip_code = component.long_name
            break
          case 'postal_code_suffix':
            if (address.zip_code) {
              address.zip_code = `${address.zip_code}-${component.long_name}`
            }
            break
          case 'locality':
            address.city = component.long_name
            break
          case 'administrative_area_level_1':
            address.state = component.short_name
            break
        }
      }

      onPlaceSelected(address)
    })
  }

  return (
    <FloatingLabel controlId='floatingAddress' label='Street and apt. #'>
      <Form.Control
        ref={inputRef}
        type='text'
        name='metadata.address_line1'
        placeholder='Street and apt. #'
        value={value}
        onChange={onChange}
        isInvalid={isInvalid}
        disabled={disabled}
      />
      <Form.Control.Feedback type='invalid'>{error}</Form.Control.Feedback>
    </FloatingLabel>
  )
}

export default GooglePlacesAutocomplete
